import { useState, useCallback, useEffect } from "react";
import api from "../services/api";

// Semanas dos gráficos de frequência e carga do Dashboard: 12 cobre um ciclo
// de treino inteiro (~3 meses). O backend aceita até 26 (weekly-stats).
export const CHART_WEEKS = 12;

/**
 * useDashboardData
 *
 * Encapsula todo o estado de dados fetchados do Dashboard e a função loadData.
 *
 * @param {object}   opts
 * @param {number}   opts.userId          - ID do usuário logado
 * @param {function} opts.onActiveSession - callback(sessionData) chamado quando há sessão ativa;
 *                                          o Dashboard chama setWorkoutSession(prev => parseActiveSession(data, prev))
 * @param {function} opts.setToast        - callback({ type, message }) para exibir erros
 */
export function useDashboardData({ userId, onActiveSession, setToast }) {
  const [loading, setLoading]                     = useState(true);
  const [loadError, setLoadError]                 = useState("");
  const [me, setMe]                               = useState(() => {
    try { return JSON.parse(localStorage.getItem("me") || "null"); } catch { return null; }
  });
  const [totalPoints, setTotalPoints]             = useState(0);
  const [pointsHistory, setPointsHistory]         = useState([]);
  const [myChallenges, setMyChallenges]           = useState([]);
  const [allChallenges, setAllChallenges]         = useState([]);
  const [ranking, setRanking]                     = useState([]);
  const [exercises, setExercises]                 = useState([]);
  const [pointsToday, setPointsToday]             = useState(0);
  const [checkinsToday, setCheckinsToday]         = useState(0);
  const [checkins, setCheckins]                   = useState([]);
  const [assignedWorkouts, setAssignedWorkouts]   = useState([]);
  const [weeklyCadenceSeries, setWeeklyCadenceSeries] = useState(null);
  const [weeklyDurationSeries, setWeeklyDurationSeries] = useState(null);
  const [weeklyVolumeSeries, setWeeklyVolumeSeries]   = useState(null);
  const [unreadFeedbacks, setUnreadFeedbacks]     = useState([]);
  const [hasDiet, setHasDiet]                     = useState(false);

  const loadData = useCallback(async () => {
    setLoadError("");
    if (!userId) {
      setMyChallenges([]);
      setAllChallenges([]);
      setRanking([]);
      setExercises([]);
      setCheckins([]);
      setAssignedWorkouts([]);
      setPointsToday(0);
      setCheckinsToday(0);
      setTotalPoints(0);
      setLoading(false);
      return;
    }

    try {
      const [
        my, all, rank, ex,
        ptsToday, checkinsTodayRes, checkinsList,
        userRes, assignmentsRes, ptsHistory, weeklyStats,
      ] = await Promise.all([
        api.get(`/users/${userId}/challenges`),
        api.get("/dashboard/challenges"),
        api.get("/exercises/ranking?limit=10"),
        api.get("/exercises"),
        api.get(`/users/${userId}/points-today`),
        api.get(`/users/${userId}/checkins-today`),
        // limit=200: o dashboard só usa checkins pra agregações de 8 semanas
        // (~56 itens) e os "últimos 5" — 200 cobre com folga; backend pagina
        // em app/utils/pagination.py (débito 009).
        api.get(`/users/${userId}/checkins?limit=200`),
        api.get("/users/me"),
        api.get(`/user-workout-plans/users/${userId}?only_active=true`),
        api.get("/users/me/points-history"),
        api.get(`/users/${userId}/weekly-stats?weeks=${CHART_WEEKS}`),
      ]);

      setMyChallenges(my.data);
      setAllChallenges(all.data);
      setRanking(rank.data);
      setExercises(ex.data);
      setPointsToday(ptsToday.data.points_today);
      setCheckinsToday(checkinsTodayRes.data.checkins_today);
      setCheckins(checkinsList.data);
      setAssignedWorkouts(assignmentsRes.data || []);
      setTotalPoints(userRes.data.points);
      setMe(userRes.data);
      setPointsHistory(Array.isArray(ptsHistory.data) ? ptsHistory.data : []);

      const cadence  = Array.isArray(weeklyStats?.data?.cadence)  ? weeklyStats.data.cadence  : null;
      const duration = Array.isArray(weeklyStats?.data?.duration) ? weeklyStats.data.duration : null;
      const volume   = Array.isArray(weeklyStats?.data?.volume)   ? weeklyStats.data.volume   : null;
      setWeeklyCadenceSeries(cadence);
      setWeeklyDurationSeries(duration);
      setWeeklyVolumeSeries(volume);

      // Sessão ativa (delegado ao Dashboard via callback para não acoplar parseActiveSession aqui)
      try {
        const act = await api.get("/workout-sessions/active");
        if (act.data?.active && onActiveSession) {
          onActiveSession(act.data);
        }
      } catch (_) {}

      // Feedbacks não lidos
      try {
        const fbRes = await api.get("/session-feedback/unread");
        setUnreadFeedbacks(fbRes.data?.feedbacks || []);
      } catch (_) {}

    } catch (err) {
      const msg = _extractErrorMessage(err);
      setLoadError(msg);
      setToast?.({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  }, [userId, onActiveSession, setToast]);

  // Carrega na montagem e sempre que userId mudar
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Verifica se aluno tem dieta (para atalho de navegação) — exposto como
  // refetchHasDiet pra recarregar depois que o aluno gera/salva uma dieta
  // pela primeira vez dentro da mesma sessão (sem isso, o ícone do nav só
  // aparecia após um reload completo da página).
  //
  // NÃO dispara sozinho no mount: GET /nutrition/diets é gated por Plus no
  // backend (require_plus_access), então aluno self-service sem Plus sempre
  // levava um 403 aqui (inofensivo — catch silencioso, hasDiet nunca é
  // exibido pra ele — mas uma requisição desperdiçada a cada carregamento
  // do dashboard). Quem decide QUANDO chamar é o Dashboard, que já sabe
  // hasPaidAccess (calculado por usePaidAccess, fora deste hook) e replica
  // a mesma condição usada em DietBannerCard/menu (!is_self_service ||
  // hasPaidAccess). Ver docs/07-issues.md.
  const checkHasDiet = useCallback(() => {
    if (!me?.role || me.role !== "aluno") return;
    api.get("/nutrition/diets")
      .then((r) => setHasDiet(r.data?.length > 0))
      .catch(() => {});
  }, [me?.role]);

  // Persiste me no localStorage para inicialização rápida
  useEffect(() => {
    if (me) localStorage.setItem("me", JSON.stringify(me));
  }, [me]);

  return {
    // estado
    loading, loadError,
    me, setMe,
    totalPoints, setTotalPoints,
    pointsHistory,
    myChallenges, setMyChallenges,
    allChallenges, setAllChallenges,
    ranking,
    exercises,
    pointsToday,
    checkinsToday, setCheckinsToday,
    checkins, setCheckins,
    assignedWorkouts, setAssignedWorkouts,
    weeklyCadenceSeries,
    weeklyDurationSeries,
    weeklyVolumeSeries,
    unreadFeedbacks, setUnreadFeedbacks,
    hasDiet, refetchHasDiet: checkHasDiet,
    // ação
    loadData,
  };
}

// ── helpers ────────────────────────────────────────────────────────────────────

function _extractErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Erro ao carregar dados";
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(" • ");
  if (typeof detail === "string") return detail;
  return "Erro ao carregar dados";
}
